<template>
    <div>
        <p class="text-body font-semibold mb-16">{{ $t('general.credentials') }}</p>
        <div class="grid grid-cols-2 gap-16 my-24">
            <div v-if="isXmlForm" class="col-span-2">
                <div class="w-full h-fit bg-primary-5 p-16 relative">
                    <copy-to-clipboard
                        :copy-value="contract.feed_url"
                    >
                        <span class="break-all">{{ contract.feed_url }}</span>
                    </copy-to-clipboard>
                </div>
            </div>
            <div v-for="contractCredential in authenticationCredentials" :key="contractCredential.sort"
                 class="col-span-2">

                <simple-button
                    look="primary"
                    type="button"
                    v-if="requiresAuthentication"
                    @click="initiateOAuth(contractCredential)"
                >
                    {{ $t('multi-posting.contracts.start_authentication') }}
                </simple-button>

                <simple-button
                    class="items-center"
                    disabled
                    icon="check-circle"
                    icon-position="left"
                    look="primary"
                    type="button"
                    v-if="isAuthenticated"
                >
                    {{ $t('multi-posting.contracts.authentication_success') }}
                </simple-button>

            </div>
            <div
                v-for="contractCredential in contractCredentials"
                :key="contractCredential.sort"
                class="col-span-2 flex flex-col gap-8"
            >
                <select-dropdown
                    :auto-translate="false"
                    :disabled="disabled"
                    :input-label="contractCredential.label + '*'"
                    :options="contractCredential.options.map(x => { return { value: x.key, label: x.label } })"
                    v-if="contractCredential.options"
                    :value="form[contractCredential.name]"
                    @change="(value) => formChanged(contractCredential.name, value)"
                />
                <simple-input
                    :disabled="disabled"
                    :errors="form.errors[contractCredential.name]"
                    :input-label="contractCredential.label + '*'"
                    :readonly="contractCredential.readonly"
                    :type="contractCredential.name.includes('password') ? 'password' : 'text'"
                    :value="form[contractCredential.name]"
                    @input="(value) => formChanged(contractCredential.name, value)"
                />
            </div>
            <div v-if="requiresFollowedInstructions" class="col-span-2">
                <checkbox
                    v-model="form['followed_instructions']"
                    @change="(checked) => formChanged('followed_instructions', checked)"
                    :disabled="disabled"
                >
                    {{ $t('multi-posting.contracts.form.follow_instructions') }}
                </checkbox>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../etc/forms/Form";
import SimpleInput from "../forms/Input.vue";
import SimpleButton from "../ui/SimpleButton.vue";
import Checkbox from "../forms/Checkbox.vue";
import SelectDropdown from "../forms/SelectDropdown.vue";
import SvgIcon from "../ui/SvgIcon.vue";
import { readonly } from "vue";
import { trim } from "lodash";
import CopyToClipboard from "../ui/CopyToClipboard.vue";

export default {
    name: 'MultiPostingDynamicContractForm',
    components: {CopyToClipboard, SelectDropdown, Checkbox, SimpleButton, SimpleInput, SvgIcon },
    props: {
        form: Form,
        contract: Object,
        disabled: Boolean,
        authDetails: Object,
    },
    data() {
        return {
            showCopiedMessage: false
        };
    },
    computed: {
        isXmlForm() {
            return this.contract && this.contract.feed_url && trim(this.contract.feed_url.length) > 0;
        },
        requiresFollowedInstructions() {
            return this.contract['manual_setup_required'];
        },
        authenticationCredentials() {
            const oauthCredential = this.contract.contract_credentials?.find(cred => (!!cred.url && cred.name != 'feed-random'))
            return oauthCredential ? [oauthCredential] : [];
        },
        contractCredentials() {
            const credentials = this.contract.contract_credentials?.filter(cred => cred.url === null)
            return credentials || [];
        },
        feedUrl() {
            return this.contract.feed_url;
        },
        followedInstructions() {
            return this.form['followed_instructions'];
        },
        formData() {
            return this.form.data;
        },
        hasHash() {
            return this.authDetails?.valid ? this.authDetails.hash.length > 0 : false
        },
        requiresAuthentication() {
            return !this.hasHash
        },
        isAuthenticated() {
            return this.hasHash
        }
    },
    methods: {
        validateForm() {
            let validForm = true;
            let errors = {};

            if (this.requiresFollowedInstructions && !this.followedInstructions) {
               validForm = false;
            }

            if (this.isXmlForm && (!this.form.data.xml_feed_url || this.form.data.xml_feed_url.length === 0 || this.form.data.xml_feed_url !== this.feedUrl)) {
                validForm = false;
            }

            if (this.contractCredentials.length > 0) {
                const result = this.validCredentials()
                errors = {...errors, ...result.errors}
                validForm = result.valid
            }

            this.$emit('validation', validForm, errors);
        },
        validCredentials() {
            let result = {valid: true, errors: {}}
            this.contractCredentials.forEach((c) => {
                const value = this.form[c.name]
                if (!value || value.length == 0) {
                    result.valid = false
                    result.errors[c.name] = [this.$t('validation.required', { attribute: c.label })]
                }
            }, this)
            return result
        },
        isFullWidth(contractCredential) {
            switch (contractCredential.name) {
                case 'xml_feed_url':
                    return true;
                default:
                    return false;
            }
        },
        initiateOAuth(credential) {
            const state = {
                contractId: this.contract.id,
                fieldName: credential.name,
            }
            const redirectUrl = this.getRedirectURL(credential.url, state)
            window.location.replace(redirectUrl)
        },
        getRedirectURL(url, state) {
            const encodedState = btoa(JSON.stringify(state))
            const callbackUrl = encodeURIComponent(
                window.location.origin + '/multi-posting?state=' + encodedState
            );
            return `${url}&callbackUrl=${callbackUrl}`;
        },
        formChanged(field, value) {
            this.$emit('formChanged', {field: field, val: value});
            this.validateForm();
        }
    },
    mounted() {
        if (this.authDetails?.valid) {
            this.formChanged(this.authDetails.fieldName, this.authDetails.hash);
        }
    },
    watch: {
        xmlDeclaration: function () {
            this.validateForm();
        },
        'contract.feed_url': {
            handler() {
                this.$nextTick(() => {
                    if (!this.$refs.textarea) return;
                    this.$refs.textarea.style.height = 'auto';
                    this.$refs.textarea.style.height = this.$refs.textarea.scrollHeight + 'px';
                });
            },
            immediate: true
        }
    },
    created() {
        this.validateForm();
    }
}
</script>
